import { useEffect, useRef, useCallback } from 'react';
import { useGameStore } from '../store/gameStore';

export function useTimer() {
  const { timeSpent, setTimeSpent, isPaused, isComplete } = useGameStore();
  const intervalRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);

  const startTimer = useCallback(() => {
    if (intervalRef.current) return;
    
    startTimeRef.current = Date.now() - timeSpent * 1000;
    
    intervalRef.current = window.setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setTimeSpent(elapsed);
    }, 1000);
  }, [timeSpent, setTimeSpent]);

  const stopTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const resetTimer = useCallback(() => {
    stopTimer();
    setTimeSpent(0);
  }, [stopTimer, setTimeSpent]);

  // Auto start/stop based on game state
  useEffect(() => {
    if (isPaused || isComplete) {
      stopTimer();
    } else {
      startTimer();
    }

    return () => stopTimer();
  }, [isPaused, isComplete, startTimer, stopTimer]);

  // Format time as MM:SS
  const formatTime = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  return {
    timeSpent,
    formattedTime: formatTime(timeSpent),
    startTimer,
    stopTimer,
    resetTimer,
    formatTime,
  };
}

